import { byId } from "./domUtils";
import type { PrototypeRuntime, Root } from "./prototypeTypes";
import {
	disposeWizardTurnstile,
	disposeModalTurnstile,
	reconcileActiveModalTurnstile,
	reconcileModalTurnstile,
	reconcileWizardTurnstile,
	restartModalTurnstile,
	restartWizardTurnstile,
} from './modalTurnstileLifecycle';
import {
	lockSubmitButton,
	unlockSubmitButton,
	type TurnstileWidgetCallbacks,
} from './turnstileWidgetRenderer';
export { lockSubmitButton, unlockSubmitButton } from './turnstileWidgetRenderer';
export type { TurnstileWidgetCallbacks } from './turnstileWidgetRenderer';

/**
 * Manages the visible/interactive Turnstile widget lifecycle for wizard modals
 * (AI Create + Community idea submission). The widget is rendered into a
 * Turnstile container when Step 3 becomes active, and the submit button stays
 * disabled until the user solves the challenge. On error/expiry, the button
 * re-locks and the widget resets so the user can try again.
 *
 * Mirrors the Landing page's early-access.js pattern:
 *   - Button starts locked (disabled) when step 3 opens
 *   - Turnstile callback(token) -> unlock
 *   - error-callback / expired-callback -> re-lock + reset
 *   - On server-side turnstile failure -> re-lock + reset for a fresh challenge
 */

export type AuthTurnstileKind = 'login' | 'register' | 'forgot' | 'resend' | 'oauthLink' | 'setPassword';

const noopCallbacks: TurnstileWidgetCallbacks = { onSuccess: () => {}, onError: () => {}, onExpired: () => {} };

// ── AI Create ──

/**
 * Render the visible Turnstile widget into the AI Create modal's Step 3 container.
 * The submit button stays locked until the widget's callback fires with a token.
 */
export async function renderAiCreateTurnstile(
	root: Root,
	runtime: PrototypeRuntime,
	callbacks: TurnstileWidgetCallbacks,
): Promise<void> {
	return reconcileWizardTurnstile(root, runtime, 'ai-create', callbacks);
}

/**
 * Reset the Turnstile widget and clear the container. Called when leaving
 * Step 3 (back navigation or modal close) so the widget re-renders fresh
 * on re-entry.
 */
export function resetAiCreateTurnstile(root: Root): void {
	disposeWizardTurnstile(root, 'ai-create');
}

/**
 * Reset the widget after a server-side turnstile failure. Re-locks the button
 * and triggers a fresh challenge.
 */
export function resetAiCreateTurnstileAfterError(root: Root, runtime: PrototypeRuntime): void {
	void restartWizardTurnstile(root, runtime, 'ai-create');
}

// ── Community Idea Submission ──

/**
 * Render the visible Turnstile widget into the idea submission modal's Step 3
 * container. Mirrors renderAiCreateTurnstile with idea-form-specific IDs/action.
 */
export async function renderIdeaTurnstile(
	root: Root,
	runtime: PrototypeRuntime,
	callbacks: TurnstileWidgetCallbacks,
): Promise<void> {
	return reconcileWizardTurnstile(root, runtime, 'submit-idea', callbacks);
}

/**
 * Reset the idea form's Turnstile widget and clear the container.
 */
export function resetIdeaTurnstile(root: Root): void {
	disposeWizardTurnstile(root, 'submit-idea');
}

/**
 * Reset the idea form's widget after a server-side turnstile failure.
 */
export function resetIdeaTurnstileAfterError(root: Root, runtime: PrototypeRuntime): void {
	void restartWizardTurnstile(root, runtime, 'submit-idea');
}

// ── Auth Modals (Login / Register / Forgot Password) ──

const AUTH_LOGIN_TURNSTILE_ACTION = "auth-login";
const AUTH_REGISTER_TURNSTILE_ACTION = "auth-register";
const AUTH_FORGOT_TURNSTILE_ACTION = "auth-password-reset";
const AUTH_SET_PASSWORD_TURNSTILE_ACTION = "auth-set-password";
export const AUTH_RESEND_VERIFICATION_TURNSTILE_ACTION = 'auth-resend-verification';

const authTurnstileConfig = {
	login: { modalId: "loginModal", containerId: "loginTurnstileContainer", buttonId: "loginSubmitBtn", statusId: "loginTurnstileStatus", statusTextId: "loginTurnstileStatusText", action: AUTH_LOGIN_TURNSTILE_ACTION },
	register: { modalId: "registerModal", containerId: "registerTurnstileContainer", buttonId: "registerSubmitBtn", statusId: "registerTurnstileStatus", statusTextId: "registerTurnstileStatusText", action: AUTH_REGISTER_TURNSTILE_ACTION },
	forgot: { modalId: "forgotModal", containerId: "forgotTurnstileContainer", buttonId: "forgotSubmitBtn", statusId: "forgotTurnstileStatus", statusTextId: "forgotTurnstileStatusText", action: AUTH_FORGOT_TURNSTILE_ACTION },
	resend: { modalId: 'registerModal', containerId: 'resendVerificationTurnstileContainer', buttonId: 'resendVerificationSubmitBtn', statusId: 'resendVerificationTurnstileStatus', statusTextId: 'resendVerificationTurnstileStatusText', action: AUTH_RESEND_VERIFICATION_TURNSTILE_ACTION },
	oauthLink: { modalId: 'oauthLinkModal', containerId: 'oauthLinkTurnstileContainer', buttonId: 'oauthLinkSubmitBtn', statusId: 'oauthLinkTurnstileStatus', statusTextId: 'oauthLinkTurnstileStatusText', action: 'auth-google-link' },
	setPassword: { modalId: 'setPasswordConfirmModal', containerId: 'setPasswordTurnstileContainer', buttonId: 'setPasswordConfirmBtn', statusId: 'setPasswordTurnstileStatus', statusTextId: 'setPasswordTurnstileStatusText', action: AUTH_SET_PASSWORD_TURNSTILE_ACTION },
} as const;

async function renderAuthTurnstile(
	root: Root,
	runtime: PrototypeRuntime,
	kind: AuthTurnstileKind,
	_callbacks: TurnstileWidgetCallbacks,
): Promise<void> {
	const config = authTurnstileConfig[kind];
	paintAuthTurnstileStatus(root, kind, null);
	return reconcileModalTurnstile(root, config.modalId, runtime);
}

/** Render the visible Turnstile widget into the Login modal. */
export async function renderLoginTurnstile(
	root: Root,
	runtime: PrototypeRuntime,
	callbacks: TurnstileWidgetCallbacks,
): Promise<void> {
	return renderAuthTurnstile(root, runtime, "login", callbacks);
}

/** Render the visible Turnstile widget into the Register modal. */
export async function renderRegisterTurnstile(
	root: Root,
	runtime: PrototypeRuntime,
	callbacks: TurnstileWidgetCallbacks,
): Promise<void> {
	return renderAuthTurnstile(root, runtime, "register", callbacks);
}

/** Render the visible Turnstile widget into the Forgot Password modal. */
export async function renderForgotTurnstile(
	root: Root,
	runtime: PrototypeRuntime,
	callbacks: TurnstileWidgetCallbacks,
): Promise<void> {
	return renderAuthTurnstile(root, runtime, "forgot", callbacks);
}

/** Render the independently-scoped challenge for resending a verification link. */
export async function renderResendVerificationTurnstile(
	root: Root,
	runtime: PrototypeRuntime,
	callbacks: TurnstileWidgetCallbacks = noopCallbacks,
): Promise<void> {
	return renderAuthTurnstile(root, runtime, 'resend', callbacks);
}

export async function renderOAuthLinkTurnstile(root: Root, runtime: PrototypeRuntime): Promise<void> {
	return renderAuthTurnstile(root, runtime, 'oauthLink', noopCallbacks);
}

/** Render the visible Turnstile widget into the Set Password confirm modal. */
export async function renderSetPasswordTurnstile(
	root: Root,
	runtime: PrototypeRuntime,
	callbacks: TurnstileWidgetCallbacks,
): Promise<void> {
	return renderAuthTurnstile(root, runtime, 'setPassword', callbacks);
}

export async function retryAuthTurnstile(root: Root, runtime: PrototypeRuntime, kind: AuthTurnstileKind): Promise<void> {
	await restartModalTurnstile(root, authTurnstileConfig[kind].modalId, runtime);
}

/** Rebind an auth modal that opened before the real site key finished loading. */
export function rerenderActiveAuthTurnstile(root: Root, runtime: PrototypeRuntime): Promise<void> | null {
	return reconcileActiveModalTurnstile(root, runtime);
}

/** Reset the Login modal's widget after a server-side turnstile failure. */
export function resetLoginTurnstileAfterError(root: Root, runtime: PrototypeRuntime): void {
	void restartModalTurnstile(root, 'loginModal', runtime);
}

/** Reset the Register modal's widget after a server-side turnstile failure. */
export function resetRegisterTurnstileAfterError(root: Root, runtime: PrototypeRuntime): void {
	void restartModalTurnstile(root, 'registerModal', runtime);
}

/** Render a fresh one-shot challenge after the existing-account password was rejected. */
export function resetOAuthLinkTurnstileAfterError(root: Root, runtime: PrototypeRuntime): void {
	void restartModalTurnstile(root, 'oauthLinkModal', runtime);
}

/** Reset the Forgot Password modal's widget after a server-side turnstile failure. */
export function resetForgotTurnstileAfterError(root: Root, runtime: PrototypeRuntime): void {
	void restartModalTurnstile(root, 'forgotModal', runtime);
}

/** Re-lock the resend control before rendering a fresh one-shot challenge. */
export function resetResendVerificationTurnstileAfterError(root: Root, runtime: PrototypeRuntime): void {
	void restartModalTurnstile(root, 'registerModal', runtime);
}

/** Reset the Set Password modal's widget after a server-side turnstile failure. */
export function resetSetPasswordTurnstileAfterError(root: Root, runtime: PrototypeRuntime): void {
	void restartModalTurnstile(root, 'setPasswordConfirmModal', runtime);
}

/** Clear an auth modal's Turnstile container (on modal close). */
export function clearAuthTurnstile(
	root: Root,
	runtime: PrototypeRuntime,
	containerId: 'loginTurnstileContainer' | 'registerTurnstileContainer' | 'forgotTurnstileContainer' | 'resendVerificationTurnstileContainer' | 'oauthLinkTurnstileContainer' | 'setPasswordTurnstileContainer',
): void {
	const kind = (Object.keys(authTurnstileConfig) as AuthTurnstileKind[]).find((candidate) => authTurnstileConfig[candidate].containerId === containerId);
	if (!kind) return;
	disposeModalTurnstile(root, authTurnstileConfig[kind].modalId, runtime);
	paintAuthTurnstileStatus(root, kind, null);
}

function paintAuthTurnstileStatus(root: Root, kind: AuthTurnstileKind, message: string | null): void {
	const config = authTurnstileConfig[kind];
	const status = byId(root, config.statusId);
	const text = byId(root, config.statusTextId);
	if (text) text.textContent = message ?? "";
	if (status) status.hidden = !message;
}
